// Shared canvas <-> pixel-buffer encode/decode helpers, extracted out of
// editor-data-fx.tsx so the texture-painting feature (fxMaterialPaintSession.ts)
// can reuse the exact same PNG encode/decode path as procedural texture
// generation and normal-map derivation, rather than a third private copy.
// This is the DOM-touching half of texture generation/painting - the pixel
// math itself (textureGenerators.ts, texturePaint.ts) stays pure and
// canvas-free so it's actually unit-testable (this repo's test environment
// has no real 2D canvas context).

/** Paints RGBA bytes into an offscreen canvas and encodes it as a PNG File. */
export async function pixelsToPngFile(pixels: Uint8ClampedArray, width: number, height: number, filename: string): Promise<File> {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not create a canvas to bake the texture.');
  // A fresh copy guarantees a plain-ArrayBuffer-backed array - `pixels` may
  // be typed against the wider ArrayBufferLike (SharedArrayBuffer-inclusive)
  // generic, which ImageData's constructor rejects.
  ctx.putImageData(new ImageData(new Uint8ClampedArray(pixels), width, height), 0, 0);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('Could not encode the generated texture.');
  return new File([blob], filename, { type: 'image/png' });
}

/** The inverse of `pixelsToPngFile`: decodes an already-uploaded image URL
 * back into RGBA bytes, so the normal-map-from-albedo bonus (and a paint
 * session seeding itself from an existing albedo) have real pixels to work
 * with. */
export async function loadImagePixels(url: string): Promise<{ pixels: Uint8ClampedArray; width: number; height: number }> {
  const response = await fetch(url);
  const blob = await response.blob();
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not read the image.');
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  return { pixels: data, width: canvas.width, height: canvas.height };
}
